import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Upload, Download, Trash2, FileText, Image, FileSpreadsheet, Paperclip } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getFileIcon = (filename) => {
  const ext = (filename || '').split('.').pop().toLowerCase();
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(ext)) return <Image className="w-4 h-4 text-blue-500" />;
  if (['xls', 'xlsx', 'csv'].includes(ext)) return <FileSpreadsheet className="w-4 h-4 text-emerald-600" />;
  if (ext === 'pdf') return <FileText className="w-4 h-4 text-red-500" />;
  return <Paperclip className="w-4 h-4 text-slate-400" />;
};

const formatSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const AttachmentPanel = ({ entityType, entityId }) => {
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [observacion, setObservacion] = useState('');
  const fileRef = useRef(null);

  useEffect(() => {
    if (entityId) loadAttachments();
  }, [entityId]);

  const loadAttachments = async () => {
    try {
      const { data } = await axios.get(
        `${BACKEND_URL}/api/${entityType}s/${entityId}/attachments`,
        {  }
      );
      setAttachments(data);
    } catch (error) {
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('observacion', observacion);

      await axios.post(
        `${BACKEND_URL}/api/${entityType}s/${entityId}/attachments`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setObservacion('');
      if (fileRef.current) fileRef.current.value = '';
      loadAttachments();
    } catch (error) {
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (att) => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/attachments/${att.id}/download`,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = att.original_filename;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
    }
  };

  const handleDelete = async (att) => {
    if (!window.confirm(`¿Eliminar "${att.original_filename}"?`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/attachments/${att.id}`);
      loadAttachments();
    } catch (error) {
    }
  };

  return (
    <div className="space-y-3" data-testid="attachment-panel">
      <div className="flex items-center gap-2 text-xs font-semibold tracking-wider uppercase text-slate-500">
        <Paperclip className="w-3.5 h-3.5" />
        Archivos Adjuntos ({attachments.length})
      </div>

      {/* Upload area */}
      <div className="border border-dashed border-slate-300 rounded-lg p-3 space-y-2 bg-slate-50/50">
        <Input
          value={observacion}
          onChange={(e) => setObservacion(e.target.value)}
          placeholder="Observación del archivo (opcional)"
          className="text-sm h-8"
          data-testid="attachment-observacion"
        />
        <div className="flex items-center gap-2">
          <input
            ref={fileRef}
            type="file"
            onChange={handleUpload}
            className="hidden"
            accept=".pdf,.xls,.xlsx,.csv,.doc,.docx,.jpg,.jpeg,.png,.gif,.zip,.rar,.txt"
            data-testid="attachment-file-input"
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="text-xs"
            data-testid="attachment-upload-btn"
          >
            <Upload className="w-3.5 h-3.5 mr-1.5" />
            {uploading ? 'Subiendo...' : 'Adjuntar archivo'}
          </Button>
          <span className="text-[10px] text-slate-400">PDF, Excel, imágenes, etc.</span>
        </div>
      </div>

      {/* File list */}
      {attachments.length > 0 && (
        <div className="space-y-1.5">
          {attachments.map((att) => (
            <div
              key={att.id}
              className="flex items-center justify-between p-2.5 bg-white border border-slate-200 rounded-md hover:border-slate-300 transition-colors group"
              data-testid={`attachment-item-${att.id}`}
            >
              <div className="flex items-center gap-2.5 min-w-0 flex-1">
                {getFileIcon(att.original_filename)}
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-slate-800 truncate">{att.original_filename}</p>
                  <div className="flex items-center gap-2 text-[10px] text-slate-400">
                    <span>{formatSize(att.size)}</span>
                    <span>{att.uploaded_by_name}</span>
                    {att.created_at && <span>{new Date(att.created_at).toLocaleDateString('es-CO')}</span>}
                  </div>
                  {att.observacion && (
                    <p className="text-xs text-slate-500 mt-0.5 italic">"{att.observacion}"</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  onClick={() => handleDownload(att)}
                  className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                  title="Descargar"
                  data-testid={`attachment-download-${att.id}`}
                >
                  <Download className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => handleDelete(att)}
                  className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                  title="Eliminar"
                  data-testid={`attachment-delete-${att.id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AttachmentPanel;
